import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  approveJoinRequest,
  getJoinRequests,
  JoinRequest,
  JoinRequestStatus,
  rejectJoinRequest,
} from 'src/apis/companyJoinRequests';
import PageContainer from 'src/pages/components/PageContainer';
import { isUserAdministrator } from 'src/utils/auth';

const STATUS_TABS: { label: string; value: JoinRequestStatus }[] = [
  { label: 'Pendentes', value: 'PENDING' },
  { label: 'Aprovadas', value: 'APPROVED' },
  { label: 'Rejeitadas', value: 'REJECTED' },
];

const statusColor = (status: JoinRequestStatus): 'warning' | 'success' | 'error' => {
  if (status === 'PENDING') return 'warning';
  if (status === 'APPROVED') return 'success';
  return 'error';
};

const statusLabel: Record<JoinRequestStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovada',
  REJECTED: 'Rejeitada',
};

const CompanyJoinRequests: FC = (): JSX.Element => {
  const [tab, setTab] = useState<JoinRequestStatus>('PENDING');
  const [items, setItems] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [rejectTarget, setRejectTarget] = useState<JoinRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const isAdmin = isUserAdministrator();

  const load = useCallback(async (status: JoinRequestStatus) => {
    setLoading(true);
    setError('');
    try {
      const data = await getJoinRequests(status);
      setItems(data);
    } catch {
      setError('Erro ao carregar solicitações.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const handleApprove = useCallback(async (req: JoinRequest) => {
    try {
      setActionLoading(req.id);
      await approveJoinRequest(req.id);
      setItems((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao aprovar solicitação.');
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectTarget) return;
    try {
      setActionLoading(rejectTarget.id);
      await rejectJoinRequest(rejectTarget.id, rejectReason);
      setItems((prev) => prev.filter((r) => r.id !== rejectTarget.id));
      setRejectTarget(null);
      setRejectReason('');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao rejeitar solicitação.');
    } finally {
      setActionLoading(null);
    }
  }, [rejectTarget, rejectReason]);

  return (
    <PageContainer title="Solicitações de Vinculação">
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        {STATUS_TABS.map((t) => (
          <Tab key={t.value} label={t.label} value={t.value} />
        ))}
      </Tabs>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && items.length === 0 && (
        <Typography color="text.secondary" textAlign="center" py={6}>
          Nenhuma solicitação {statusLabel[tab].toLowerCase()}.
        </Typography>
      )}

      {!loading && !error && items.map((req) => (
        <Box
          key={req.id}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: 2,
          }}
        >
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {req.requester_name || req.requester_email}
              </Typography>
              <Chip
                label={statusLabel[req.status]}
                color={statusColor(req.status)}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {req.requester_email}
            </Typography>
            {isAdmin && req.empresa && (
              <Typography variant="body2" color="text.secondary">
                Empresa: <strong>{req.empresa.trade_name}</strong>
              </Typography>
            )}
            {req.rejection_reason && (
              <Typography variant="body2" color="error.main" mt={0.5}>
                Motivo de rejeição: {req.rejection_reason}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              Solicitado em: {new Date(req.created_at).toLocaleString('pt-BR')}
              {req.reviewed_at && ` · Revisado em: ${new Date(req.reviewed_at).toLocaleString('pt-BR')}`}
            </Typography>
          </Box>

          {req.status === 'PENDING' && (
            <Box display="flex" gap={1} flexShrink={0}>
              <Tooltip title="Aprovar">
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<Check />}
                  onClick={() => handleApprove(req)}
                  disabled={actionLoading === req.id}
                >
                  {actionLoading === req.id ? 'Aprovando…' : 'Aprovar'}
                </Button>
              </Tooltip>
              <Tooltip title="Rejeitar">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Close />}
                  onClick={() => { setRejectTarget(req); setRejectReason(''); }}
                  disabled={actionLoading === req.id}
                >
                  Rejeitar
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}

      <Dialog
        open={Boolean(rejectTarget)}
        onClose={() => { if (!actionLoading) { setRejectTarget(null); setRejectReason(''); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rejeitar solicitação</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>
            Deseja rejeitar a solicitação de vinculação de{' '}
            <strong>{rejectTarget?.requester_name || rejectTarget?.requester_email}</strong>?
          </DialogContentText>
          <TextField
            label="Motivo da rejeição (opcional)"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={Boolean(actionLoading)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setRejectTarget(null); setRejectReason(''); }}
            disabled={Boolean(actionLoading)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={Boolean(actionLoading)}
            startIcon={<Close />}
          >
            {actionLoading ? 'Rejeitando…' : 'Confirmar rejeição'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default CompanyJoinRequests;
